import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import adminMaterialStyleService from '../../services/api/adminMaterialStyleService';
import { AdminImageStyle, ImageStyleCreateRequest, ImageStyleUpdateRequest, ImageStyleCategory } from '../../services/api/adminImageStyleService';
import ImageUpload, { ImageUploadApi } from './ImageUpload';
import {
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskySwitch,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
  useConfirm,
} from '../tasky';

interface StyleFormData {
  name: string;
  category_id: number | undefined;
  prompt_template: string;
  description: string;
  reference_image_url: string;
  thumbnail_url: string;
  sort_order: number;
  is_active: boolean;
}

const MaterialStyleList: React.FC = () => {
  const confirm = useConfirm();
  const [styles, setStyles] = useState<AdminImageStyle[]>([]);
  const [categories, setCategories] = useState<ImageStyleCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState<AdminImageStyle | null>(null);

  const [formData, setFormData] = useState<StyleFormData>({
    name: '',
    category_id: undefined,
    prompt_template: '',
    description: '',
    reference_image_url: '',
    thumbnail_url: '',
    sort_order: 0,
    is_active: true,
  });

  const materialUploadApi = useMemo<ImageUploadApi>(() => ({
    uploadImage: (file, type) => adminMaterialStyleService.uploadImage(file, type),
    getImageUrl: (path) => adminMaterialStyleService.getImageUrl(path),
  }), []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [stylesData, categoriesData] = await Promise.all([
        adminMaterialStyleService.getAllStyles(),
        adminMaterialStyleService.getActiveCategories(),
      ]);
      setStyles(stylesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to fetch material styles:', err);
      setError('素材スタイルの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = () => {
    setEditingStyle(null);
    setFormData({
      name: '',
      category_id: categories.length > 0 ? categories[0].id : undefined,
      prompt_template: '',
      description: '',
      reference_image_url: '',
      thumbnail_url: '',
      sort_order: styles.length > 0 ? Math.max(...styles.map(s => s.sort_order)) + 1 : 1,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (style: AdminImageStyle) => {
    setEditingStyle(style);
    setFormData({
      name: style.name,
      category_id: style.category_id || undefined,
      prompt_template: style.prompt_template ?? '',
      description: style.description || '',
      reference_image_url: style.reference_image_url || '',
      thumbnail_url: style.thumbnail_url || '',
      sort_order: style.sort_order,
      is_active: style.is_active,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id,
        prompt_template: formData.prompt_template,
        description: formData.description || undefined,
        reference_image_url: formData.reference_image_url || undefined,
        thumbnail_url: formData.thumbnail_url || undefined,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingStyle) {
        await adminMaterialStyleService.updateStyle(editingStyle.id, payload as ImageStyleUpdateRequest);
      } else {
        await adminMaterialStyleService.createStyle(payload as ImageStyleCreateRequest);
      }
      setIsModalOpen(false);
      setEditingStyle(null);
      await fetchData();
    } catch (err) {
      console.error('素材スタイルの保存に失敗しました:', err);
      setError('素材スタイルの保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (id: number) => {
    setIsLoading(true);
    try {
      await adminMaterialStyleService.toggleStyle(id);
      await fetchData();
    } catch (err) {
      console.error('素材スタイルの状態変更に失敗しました:', err);
      setError('素材スタイルの状態変更に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (await confirm({ title: `素材スタイル「${name}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' })) {
      setIsLoading(true);
      try {
        await adminMaterialStyleService.deleteStyle(id);
        await fetchData();
      } catch (err) {
        console.error('素材スタイルの削除に失敗しました:', err);
        setError('素材スタイルの削除に失敗しました');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStyle(null);
  };

  const getCategoryName = (categoryId?: number) => {
    if (!categoryId) return '-';
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '-';
  };

  if (isLoading && styles.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <TaskySpinner size="lg" aria-label="読み込み中" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-dark">素材スタイル一覧</h2>
          <TaskyBadge variant="warm">{styles.length}件</TaskyBadge>
        </div>
        <TaskyButton
          size="sm"
          onClick={handleCreate}
          disabled={isLoading}
        >
          <Plus />
          新規スタイル追加
        </TaskyButton>
      </div>

      {error && (
        <TaskyErrorState
          className="mb-4"
          description={error}
          action={
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-gray-400 transition-colors hover:text-gray-600"
              aria-label="エラーを閉じる"
            >
              <X className="h-4 w-4" />
            </button>
          }
        />
      )}

      {styles.length > 0 ? (
        <TaskyTable>
          <TaskyTableHead>
            <tr>
              <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>カテゴリ</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>スタイル名</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>アクション</TaskyTableHeaderCell>
            </tr>
          </TaskyTableHead>
          <TaskyTableBody>
            {styles.map(style => (
              <TaskyTableRow key={style.id}>
                <TaskyTableCell className="text-gray-500">{style.id}</TaskyTableCell>
                <TaskyTableCell>
                  {getCategoryName(style.category_id) !== '-' ? (
                    <TaskyBadge className="bg-primary/10 text-primary">
                      {getCategoryName(style.category_id)}
                    </TaskyBadge>
                  ) : (
                    '-'
                  )}
                </TaskyTableCell>
                <TaskyTableCell className="font-medium">{style.name}</TaskyTableCell>
                <TaskyTableCell className="text-gray-600">
                  {style.description ? (style.description.length > 50 ? style.description.substring(0, 50) + '...' : style.description) : '-'}
                </TaskyTableCell>
                <TaskyTableCell className="text-gray-500">{style.sort_order}</TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge
                    className={style.is_active
                      ? 'bg-success/10 text-success'
                      : 'bg-gray-100 text-gray-500'}
                  >
                    {style.is_active ? '有効' : '無効'}
                  </TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary transition-colors hover:text-primary-hover disabled:cursor-not-allowed disabled:text-gray-300"
                      onClick={() => handleEdit(style)}
                      disabled={isLoading}
                    >
                      編集
                    </button>
                    <span className="text-gray-300">|</span>
                    <TaskySwitch
                      checked={style.is_active}
                      onChange={() => handleToggle(style.id)}
                      disabled={isLoading}
                      aria-label={style.is_active ? '無効にする' : '有効にする'}
                      title={style.is_active ? '無効にする' : '有効にする'}
                    />
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      className="text-sm font-medium text-destructive transition-colors hover:text-destructive/80 disabled:cursor-not-allowed disabled:text-gray-300"
                      onClick={() => handleDelete(style.id, style.name)}
                      disabled={isLoading}
                    >
                      削除
                    </button>
                  </div>
                </TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      ) : (
        <TaskyEmptyState title="素材スタイルデータがありません。" />
      )}

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-tasky-lg bg-white shadow-card">
            <div className="flex shrink-0 items-center justify-between border-b border-warm-border px-6 py-4">
              <h5 className="text-lg font-semibold text-dark">
                {editingStyle ? '素材スタイル編集' : '新規素材スタイル作成'}
              </h5>
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleCloseModal}
                disabled={isLoading}
                aria-label="閉じる"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
              <div className="space-y-4 overflow-y-auto px-6 py-4">
                <TaskyFormGroup label="スタイル名" required htmlFor="mat-style-name">
                  <TaskyInput
                    type="text"
                    id="mat-style-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isLoading}
                  />
                </TaskyFormGroup>

                <TaskyFormGroup label="カテゴリ" htmlFor="mat-style-category">
                  <TaskySelect
                    id="mat-style-category"
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value ? parseInt(e.target.value) : undefined })}
                    disabled={isLoading}
                  >
                    <option value="">-- 選択してください --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </TaskySelect>
                </TaskyFormGroup>

                <TaskyFormGroup label="プロンプトテンプレート" required htmlFor="mat-style-prompt">
                  <TaskyTextarea
                    id="mat-style-prompt"
                    value={formData.prompt_template}
                    onChange={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
                    required
                    disabled={isLoading}
                    rows={6}
                    className="font-mono text-sm"
                  />
                </TaskyFormGroup>

                <TaskyFormGroup label="説明" htmlFor="mat-style-desc">
                  <TaskyTextarea
                    id="mat-style-desc"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={isLoading}
                    rows={3}
                  />
                </TaskyFormGroup>

                <ImageUpload
                  label="参照画像"
                  type="reference"
                  value={formData.reference_image_url}
                  onChange={(path) => setFormData({ ...formData, reference_image_url: path })}
                  disabled={isLoading}
                  helpText="スタイルの参照画像（任意）"
                  api={materialUploadApi}
                  pathPrefix="material-styles/"
                />

                <ImageUpload
                  label="サムネイル"
                  type="thumbnail"
                  value={formData.thumbnail_url}
                  onChange={(path) => setFormData({ ...formData, thumbnail_url: path })}
                  disabled={isLoading}
                  helpText="一覧表示用のサムネイル画像（任意）"
                  api={materialUploadApi}
                  pathPrefix="material-styles/"
                />

                <TaskyFormGroup label="並び順" htmlFor="mat-style-order">
                  <TaskyInput
                    type="number"
                    id="mat-style-order"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    disabled={isLoading}
                    min={0}
                  />
                </TaskyFormGroup>

                <TaskySwitch
                  label="有効"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={isLoading}
                />
              </div>
              <div className="flex shrink-0 justify-end gap-3 border-t border-warm-border bg-warm-bg px-6 py-4">
                <TaskyButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  キャンセル
                </TaskyButton>
                <TaskyButton
                  type="submit"
                  size="sm"
                  disabled={isLoading || !formData.name || !formData.prompt_template}
                >
                  {isLoading ? '保存中...' : '保存'}
                </TaskyButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialStyleList;
